//region :: Imports ::
import React, {createRef, ReactElement, RefObject} from 'react';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import MForm from '../../Components/Toolkit/Form';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MComponent from '../../Toolkit/Components/Component';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import MFormSection from '../../Toolkit/Components/Controls/FormSection';
import {MFormOutcome} from '../../Toolkit/Components/Controls/Form';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import withRouter from '../../Toolkit/WithRouter';
import {FieldProps, FormikHelpers} from 'formik';
import * as Yup from 'yup';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import {CreateRequestRequest, RequestClient, StatusChangeEmail, UpdateRequestRequest} from '../../Api/RequestClients';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import {MNullable, toErrorMessage} from '../../Toolkit/Utils';
import {RequestReply, UpdateRequestValidator} from '../../Api/ControllerTypes/RequestTypes';
import MFormFieldControl from '../../Toolkit/Components/Controls/FormFieldControl';
import MTextBox from '../../Toolkit/Components/Controls/TextBox';
import {Request} from '../../Api/ControllerTypes/RequestTypes';
import MNumericBox from '../../Components/Toolkit/NumericBox';
import MTextArea from '../../Toolkit/Components/Controls/TextArea';
import MDropDown from '../../Toolkit/Components/Controls/DropDown';
import MButton from '../../Toolkit/Components/Controls/Button';
import NoteListControl from '../../Components/NoteListControl';
import AuditLogControl from '../../Components/AuditLogControl';
import FileListControl from '../../Components/FileListControl';
import {CreateLockRequest, LockClient} from '../../Api/LockClient';
import SafeDigAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import {MBackIconName, MNavigateToIconName, MSaveIconName, MSuccessIcon, MWarningIcon} from '../../Toolkit/Icons';
import OkCancelDialog from '../../Dialogs/OkCancelDialog';
import {FileData} from '../../Api/ControllerTypes/FileTypes';
import FileSelectionDialog from '../../Dialogs/FileSelectionDialog';
import DrawingControl from '../../Components/DrawingControl';
import CrewSelectionDialog from '../../Dialogs/CrewSelectionDialog';
import {CrewContactData} from '../../Api/ControllerTypes/WorkMailTypes';
import {DrawingClient, SendToSubRequest} from '../../Api/DrawingClient';
import MAutoComplete from '../../Components/Toolkit/AutoComplete';
import MessageDialog from '../../Dialogs/MessageDialog';
import EnumContext from '../../Context/enum-context';
import {Enumeration, QuoteStatusReply} from '../../Api/ControllerTypes/EnumerationTypes';
import {EnumerationClient} from '../../Api/EnumerationClient';
import MDropDownMulti from '../../Components/Toolkit/DropDownMulti';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import {Divider} from 'primereact/divider';
import {AddressReply, LocationReply} from '../../Api/ControllerTypes/LocationTypes';
import {LocationClient} from '../../Api/LocationClient';
import MGoogleMap from '../../Components/Toolkit/GoogleMap';
import MCalendar from '../../Components/Toolkit/Calendar';
import MNSwagReplyResult from '../../Toolkit/Api/NSwagReplyResult';
import NoteDialog from '../../Dialogs/NoteDialog';
import {CreateNoteRequest, NoteClient} from '../../Api/NoteClient';
import {NoteReply} from '../../Api/ControllerTypes/NoteTypes';
import MAlertDialog from '../../Components/Toolkit/AlertDialog';
import AlertDialog from '../../Components/Toolkit/AlertDialog';
import {Project, ProjectMultiReply} from '../../Api/ControllerTypes/ProjectTypes';
import {ProjectClient} from '../../Api/ProjectClient';

//endregion

/** */
type RouteParams = {
    id: string;
};

/** */
enum PageActions {
    None = 0,
    Saving = 1 << 1,
    Refreshing = 1 << 2
}

/** */
type Props = WithMsalProps & WithRouterProps<RouteParams> & {}

/** */
type State = {
    pageTitle: string;
    pageSubtitle: string;
    page: MPageProps;
    form: {
        id: string;
        data: Request;
        validationSchema: any;
    };
    pageActions: PageActions;
    quoteStatuses: Enumeration[];
    map: {
        lat: number;
        lng: number;
        zoom: number;
        showMarker: boolean;
    };
    updateReply?: RequestReply;
    projectList: Project[];
}

/** */
class EditRequestPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private updateValidationSchema: any = Yup.object().shape({
        exchange: UpdateRequestValidator.exchange,
        slaType: UpdateRequestValidator.slaType,
        projectValue: UpdateRequestValidator.projectValue,
        workStream: UpdateRequestValidator.workStream,
        workDescription: UpdateRequestValidator.workDescription,
        estimatedDays: UpdateRequestValidator.estimatedDays,
        address1: UpdateRequestValidator.address1,
        address2: UpdateRequestValidator.address2,
        address3: UpdateRequestValidator.address3,
        city: UpdateRequestValidator.city,
        county: UpdateRequestValidator.county,
        postcode: UpdateRequestValidator.postcode,
        northing: UpdateRequestValidator.northing,
        easting: UpdateRequestValidator.easting,
        gridReference: UpdateRequestValidator.gridReference,
        addRecipients: UpdateRequestValidator.addRecipient,
        costClaimable: UpdateRequestValidator.costClaimable,
        quoteValue: UpdateRequestValidator.quoteValue,
        jobType: UpdateRequestValidator.jobType,
        invoiceNumber: UpdateRequestValidator.invoiceNumber,
        noParkingRequired: UpdateRequestValidator.noParkingRequired,
        busStop: UpdateRequestValidator.busStop
    });

    private readonly m_routeParams: RouteParams;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_formRef: RefObject<MForm<Request>>;
    private readonly m_noteListRef: RefObject<NoteListControl>;
    private readonly m_fileSelectRef: RefObject<FileSelectionDialog>;
    private readonly m_noteDialogRef: RefObject<NoteDialog>;
    private readonly m_fileToSubSelectRef: RefObject<FileSelectionDialog>;
    private readonly m_crewSelectRef: RefObject<CrewSelectionDialog>;
    private readonly m_messageDialogRef: RefObject<MessageDialog>;
    private readonly m_requestClient: NSwagAdapter<RequestClient>;
    private readonly m_lockClient: NSwagAdapter<LockClient>;
    private readonly m_drawingClient: NSwagAdapter<DrawingClient>;
    private readonly m_enumerationClient: NSwagAdapter<EnumerationClient>;
    private readonly m_locationClient: NSwagAdapter<LocationClient>;
    private readonly m_noteClient: NSwagAdapter<NoteClient>;
    private readonly m_projectClient: NSwagAdapter<ProjectClient>;
    private readonly m_okCancelDialogRef: RefObject<OkCancelDialog>;
    private readonly m_alertDialogRef: RefObject<MAlertDialog>;
    private m_locationValue: string;
    private m_messageShowing: boolean;
    private m_oldQuoteStatus: number;
    private m_quoteSetAsCompeleted: boolean;
    private m_selectedFiles: FileData[];
    private m_selectedCrews: CrewContactData | undefined;
    
    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_routeParams = this.props.match.params;
        this.m_pageRef = createRef<MPage>();
        this.m_formRef = createRef<MForm<Request>>();
        this.m_noteListRef = createRef<NoteListControl>();
        this.m_fileSelectRef = createRef<FileSelectionDialog>();
        this.m_noteDialogRef = createRef<NoteDialog>();
        this.m_fileToSubSelectRef = createRef<FileSelectionDialog>();
        this.m_crewSelectRef = createRef<CrewSelectionDialog>();
        this.m_messageDialogRef = createRef<MessageDialog>();
        this.m_alertDialogRef = createRef<MAlertDialog>();
        this.m_locationValue = '';
        this.m_messageShowing = false;
        this.m_oldQuoteStatus = -1;
        this.m_requestClient = new NSwagAdapter<RequestClient>(new RequestClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_lockClient = new NSwagAdapter<LockClient>(new LockClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_drawingClient = new NSwagAdapter<DrawingClient>(new DrawingClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_enumerationClient = new NSwagAdapter<EnumerationClient>(new EnumerationClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_locationClient = new NSwagAdapter<LocationClient>(new LocationClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_noteClient = new NSwagAdapter<NoteClient>(new NoteClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_projectClient = new NSwagAdapter<ProjectClient>(new ProjectClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_okCancelDialogRef = createRef<OkCancelDialog>();
        this.m_quoteSetAsCompeleted = false;
        this.m_selectedFiles = [];
        this.m_selectedCrews = undefined;

        this.initializeState();
    }

    /** */
    private bindMethods(): void {

        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.close_Click = this.close_Click.bind(this);
        this.form_onBeforeSubmit = this.form_onBeforeSubmit.bind(this);
        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.form_onAfterSubmit = this.form_onAfterSubmit.bind(this);
        this.form_onError = this.form_onError.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.loadPageData = this.loadPageData.bind(this);
        this.toolbar_onBackClick = this.toolbar_onBackClick.bind(this);
        this.dialog_OkClick = this.dialog_OkClick.bind(this);
        this.toolbar_onRefreshClick = this.toolbar_onRefreshClick.bind(this);
        this.isNavigateDisabled = this.isNavigateDisabled.bind(this);
        this.isQuoteDisabled = this.isQuoteDisabled.bind(this);
        this.isJobBooked = this.isJobBooked.bind(this);
        this.toolbar_bookJob_Click = this.toolbar_bookJob_Click.bind(this);
        this.toolbar_onSaveClick = this.toolbar_onSaveClick.bind(this);
        this.onNavigate_Click = this.onNavigate_Click.bind(this);
        this.getAddress_Click = this.getAddress_Click.bind(this);
        this.quoteStatus_Change = this.quoteStatus_Change.bind(this);
        this.jobStatus_Change = this.jobStatus_Change.bind(this);
        this.onSelectCrew = this.onSelectCrew.bind(this);
        this.onSelectCrewDay = this.onSelectCrewDay.bind(this);
        this.subJobNavigate = this.subJobNavigate.bind(this);
        this.clearLock = this.clearLock.bind(this);
        this.onUnload = this.onUnload.bind(this);
        this.fileSelectionChange = this.fileSelectionChange.bind(this);
        this.onFileSelectionComplete = this.onFileSelectionComplete.bind(this);
        this.onSendToSub = this.onSendToSub.bind(this);
        this.onSendToSubCrewSelectClose = this.onSendToSubCrewSelectClose.bind(this);
        this.onSendToSubFileSelectClose = this.onSendToSubFileSelectClose.bind(this);
        this.crewSelectionChange = this.crewSelectionChange.bind(this);
        this.showMessages = this.showMessages.bind(this);
    }

    /** */
    private initializeState(): void {
        Yup.addMethod(Yup.string, 'checkDuplicate', function validateEstimate() {
            return this.test('estimate-duplicate', 'Estimate must be unique', function(value) {
                return new Promise((resolve, reject) => {
                    if(value === 'Test') {
                        reject('Estimate must be unique');
                    }
                    resolve(true);
                });
            });
        });
        //  Page
        const page: MPageProps = {locked: false};

        const data: Request = {
            id: '',
            estimate: '',
            additionalRef: '',
            locked: false,
            requestor: '',
            exchange: '',
            slaType: 0,
            projectValue: 0,
            quoteStatus: 0,
            quoteCompleteDate: null,
            workStream: 3,
            costClaimable: 0,
            quoteValue: 0,
            jobPriority: -1,
            jobType: [],
            jobDetail: '',
            jobStatus: 0,
            workDescription: '',
            estimatedDays: 0,
            address1: '',
            address2: '',
            address3: '',
            city: '',
            county: '',
            postcode: '',
            northing: 0,
            easting: 0,
            gridReference: '',
            dueDate: new Date(),
            slipNumber: '',
            jobStartDate: null,
            endType: 0,
            jobEndDate: null,
            selectedDays: [],
            noParkingRequired: false,
            weekendWorks: false,
            busStop: -1,
            dayOnly: false,
            civilsName: '',
            civilsEmail: '',
            civilsPhone: '',
            siteDocsComplete: false,
            invoiceNumber: '',
            parent: '',
            submittedDate: null,
            createdOn: null,
            addRecipients: '',
            permitRef: '',
            permitStatus: -1,
            drawingRef: '',
            resources: [],
            commercialStatus: 0,
            externalInvoicesReceived: 0,
            jobCompletedDate: null,
            commercialCompleteDate: null,
            dfeStatus: 0,
            worksTotalCost: 0,
            uplift: 0,
            dfeValue: 0,
            dfePartialReason: 0,
            dfePartialInfo: '',
            sentToDepotnet: false,
            actualCost: 0,
            drawingsBy: []
        };
        
        this.state = {
            pageTitle: '',
            pageSubtitle: '',
            page: page,
            form: {
                id: 'requestForm',
                data: data,
                validationSchema: this.updateValidationSchema,
            },
            pageActions: PageActions.None,
            quoteStatuses: [],
            map: {
                lat: 53.4591,
                lng: -2.8571,
                zoom: 18,
                showMarker: false
            },
            updateReply: undefined,
            projectList: []
        };

    }

    public componentDidMount(): void {
        window.addEventListener("unload", this.onUnload);
    }

    public componentWillUnmount(): void {
        window.removeEventListener("unload", this.onUnload);
        this.clearLock();
    }

    private async onUnload(): Promise<void> {
        await this.clearLock();
    }

    private async clearLock(): Promise<void> {
        if(this.state.form.data.id !== ''){
            await this.m_lockClient.call(client => client.delete(new CreateLockRequest(
                {
                    itemId: this.state.form.data.id
                })
            ));
        }
    }

    private form_onBeforeSubmit(fieldValues: Request, formikHelpers: FormikHelpers<Request>): boolean {
        this.safeSetState(draft => draft.pageActions !== PageActions.Saving);
        return false;
    }

    private async form_onSubmit(fieldValues: Request, formikHelpers: FormikHelpers<Request>): Promise<void> {
        const updateRequest = new UpdateRequestRequest({
            id: fieldValues.id,
            estimate: fieldValues.estimate,
            additionalRef: fieldValues.additionalRef,
            exchange: fieldValues.exchange,
            slaType: fieldValues.slaType,
            projectValue: fieldValues.projectValue,
            quoteStatus: fieldValues.quoteStatus,
            workStream: fieldValues.workStream,
            costClaimable: fieldValues.costClaimable,
            quoteValue: fieldValues.quoteValue,
            jobPriority: fieldValues.jobPriority,
            jobType: fieldValues.jobType,
            jobDetail: fieldValues.jobDetail,
            workDescription: fieldValues.workDescription,
            estimatedDays: fieldValues.estimatedDays,
            address1: fieldValues.address1,
            address2: fieldValues.address2,
            address3: fieldValues.address3,
            city: fieldValues.city,
            county: fieldValues.county,
            postcode: fieldValues.postcode,
            northing: fieldValues.northing,
            easting: fieldValues.easting,
            gridReference: fieldValues.gridReference,
            dueDate: fieldValues.dueDate ?? new Date(),
            slipNumber: fieldValues.slipNumber,
            jobStartDate: fieldValues.jobStartDate ?? undefined,
            endType: fieldValues.endType ?? 0,
            jobEndDate: fieldValues.jobEndDate ?? undefined,
            selectedDays: fieldValues.selectedDays,
            noParkingRequired: fieldValues.noParkingRequired,
            weekendWorks: fieldValues.weekendWorks,
            busStop: fieldValues.busStop,
            dayOnly: fieldValues.dayOnly,
            civilsName: fieldValues.civilsName,
            civilsEmail: fieldValues.civilsEmail,
            civilsPhone: fieldValues.civilsPhone,
            siteDocsComplete: fieldValues.siteDocsComplete,
            invoiceNumber: fieldValues.invoiceNumber,
            jobStatus: fieldValues.jobStatus,
            addRecipients: fieldValues.addRecipients?.trim().toLowerCase() ?? ''
        });

        const result = await this.m_requestClient.callWithReply(RequestReply, (client => client.update(updateRequest)));

        result.throwIfFailed()

        this.safeSetState(draft => draft.updateReply = result.reply!)

        //clear dirty flag
        formikHelpers.resetForm();
    }

    private form_onAfterSubmit(outcome: MFormOutcome, fieldValues: Request, formikHelpers: FormikHelpers<Request>, exception: any | undefined): void {
        if(outcome === 'success') {
            if(this.state.updateReply?.estimateExists === null || this.state.updateReply?.estimateExists === undefined || this.state.updateReply?.estimateExists === "") {
                this.m_pageRef.current!.messageBar.success(`Save complete.`);
            } else {
                //Request already exists.
                this.m_alertDialogRef.current!.show(
                    'TM CAD Request Already Exists',
                    `The requested Estimate already exists in the database. Do you wish to Open the existing record or Change the entered data?`,
                    MWarningIcon,
                    <React.Fragment>
                        <MButton label="Open Record"
                                 color="success"
                                 icon={MNavigateToIconName}
                                 disabled={false}
                                 onClick={this.openRecordButton_onClick}/>
                        <MSpacer/>
                        <MButton label="Change Data"
                                 color="success"
                                 icon={MNavigateToIconName}
                                 disabled={false}
                                 onClick={this.closeMessageButton_onClick}/>
                    </React.Fragment>);
            }
        }
        this.safeSetState(draft => draft.pageActions = PageActions.None);

        this.loadPageData();
    }

    private form_onError(exception: any): boolean
    {
        const handled = false;
        this.m_pageRef.current!.messageBar.error(toErrorMessage(exception));
        return handled;
    }

    private async loadPageData(): Promise<void> {
        const id = this.props.match.params.id;

        const quoteStatusResult = await this.m_enumerationClient.callWithReply(QuoteStatusReply,
            (client) => client.getQuotationStatus(id));
        quoteStatusResult.throwIfFailed();

        //Pull request data
        const result = await this.m_requestClient.callWithReply(RequestReply, (client) => client.get(id));
        result.throwIfFailed();

        result.reply!.request!.costClaimable = result.reply!.request!.costClaimable ?? 0;
        result.reply!.request!.jobType = result.reply!.request!.jobType ?? 0;
        result.reply!.request!.gridReference = result.reply!.request!.gridReference ?? '';
        result.reply!.request!.siteDocsComplete = result.reply!.request!.siteDocsComplete ?? false;

        //Get location data
        const easting = result.reply?.request?.easting ?? 0;
        const northing = result.reply?.request?.northing ?? 0;
        const location = await this.m_locationClient.callWithReply(LocationReply,
            (client) => client.bngToLatLong(easting, northing))
        location.throwIfFailed();

        const projectResult = await this.m_projectClient.callWithReply(ProjectMultiReply, (client) => client.getAll());
        projectResult.throwIfFailed();

        this.safeSetState((draft: State) => {

            draft.pageTitle = 'Edit TM CAD Request';
            draft.pageSubtitle = `Edit Request: ${result.reply!.request!.estimate}`;
            draft.form.data = result.reply!.request!;
            draft.quoteStatuses = quoteStatusResult.reply!.quoteStatuses;

            draft.map.lat = location.reply!.location!.lat;
            draft.map.lng = location.reply!.location!.long;
            draft.map.showMarker = true;
            draft.projectList = projectResult.reply!.projects

        });
        this.m_quoteSetAsCompeleted = false;
        this.m_locationValue = result.reply!.request!.gridReference;

        this.showMessages(result.reply!.request!);
    }

    private toolbar_onBackClick(): void {
        if(this.m_formRef.current?.getIsDirty()) {
            this.m_okCancelDialogRef.current!.show(
                'Data Changed',
                `Data has been changed but not saved. Do you still wish to exit?`,
                MSuccessIcon
                );
            return;
        }

        this.props.history.goBack();
    }

    private dialog_OkClick(): void {
        this.props.history.goBack();
    }

    private async toolbar_onSaveClick(): Promise<void> {
        if(this.m_quoteSetAsCompeleted) {
            this.m_oldQuoteStatus = this.state.form.data.quoteStatus;
            //Get files to send from user and email to planner.
            this.m_fileSelectRef.current!.show(this.props.match.params.id,
                'Please select files to send with the completion E-mail:');
        } else {
            await this.m_formRef.current!.submit();
            if(!(await this.m_formRef.current!.validate())) {
                this.m_messageDialogRef.current!.show("Validation Error",
                    "The form data is invalid. Please correct any field (red) errors before saving.");
            }
        }
    }

    /** */
    private async toolbar_onRefreshClick(): Promise<void> {

        this.safeSetState((draft: State) => {
            draft.pageActions |= PageActions.Refreshing;
        });

        try {
            await this.loadPageData();
        } finally {

            this.safeSetState((draft: State) => {
                draft.pageActions &= ~PageActions.Refreshing;
            });
        }
    }

    private async toolbar_bookJob_Click(): Promise<void> {
        this.props.navigate(`/requests/job/${this.state.form.data.id}`);
    }

    private async page_onLoadData(): Promise<void> {
        return this.loadPageData()
    }

    private showMessages(requestData: Request): void {
        this.m_pageRef.current?.messageBar.close();
        this.m_messageShowing = false;
        if(requestData.jobStatus !== 0) {
            this.m_messageShowing = true;
            this.m_pageRef.current?.messageBar.warning(
                "A booking for this request has already been created. Please refer to the booking section for more detail",
                true,
                false
            );
        }
    }

    /** */
    private getToolbarTemplate(): ReactElement<MToolbar> {

        const jobReadyToRaise = this.state.form.data.quoteStatus === 3
            && this.state.form.data.jobStatus === 0;

        return (
            <React.Fragment>
                <MToolbar>
                    <MButton label="Back"
                             color="secondary"
                             iconOnly={true}
                             icon={MBackIconName}
                             tooltip="Go Back"
                             disabled={false}
                             onClick={this.toolbar_onBackClick}/>
                    <MSpacer/>
                    <MButton label="Save"
                             color="success"
                             iconOnly={true}
                             icon={MSaveIconName}
                             tooltip="Save"
                             disabled={this.isFormDisabled}
                             isBusy={this.isSaving}
                             onClick={this.toolbar_onSaveClick}/>
                    <MSpacer/>
                    <MButton label="Book Job"
                             color="primary"
                             iconOnly={true}
                             icon={'pi pi-book'}
                             tooltip="Create Job from completed quote."
                             disabled={this.isFormDisabled || !jobReadyToRaise}
                             onClick={this.toolbar_bookJob_Click}/>
                    <MSpacer/>

                </MToolbar>
            </React.Fragment>
        );

    }

    private openRecordButton_onClick(): void {
        this.props.navigate(`/requests/${this.state.updateReply?.estimateExists}`);
    }

    private closeMessageButton_onClick(): void {
        this.m_alertDialogRef.current!.hide();
    }

    private async close_Click(): Promise<void> {
        this.props.history.goBack();
    }

    private getAddress_Click(fieldProps: FieldProps): void {
        this.m_locationClient.callWithReply(AddressReply,
            (client) => client.bngToAddress(fieldProps.form.values.easting, fieldProps.form.values.northing))
            .then((reply: MNSwagReplyResult<AddressReply>) => {
                reply.throwIfFailed();

                let address = reply.reply;
                if(address!.address!.addressValid) {
                    fieldProps.form.setFieldValue("address1", address!.address!.address1 ?? '')
                    fieldProps.form.setFieldValue("address2", address!.address!.address2 ?? '')
                    fieldProps.form.setFieldValue("address3", address!.address!.address3 ?? '')
                    fieldProps.form.setFieldValue("city", address!.address!.city ?? '')
                    fieldProps.form.setFieldValue("county", address!.address!.county ?? '')
                    fieldProps.form.setFieldValue("postcode", address!.address!.postcode ?? '')
                } else {
                    alert("No street address can be found for the specified location!");
                }

            });
    }

    private async quoteStatus_Change(newValue: MNullable<number>): Promise<void> {
        if(newValue === 2) { //On Hold
            this.m_noteListRef.current?.requestNote('Please enter information requirement.', false);
        } else if(newValue === 5) { //Cancelled
            this.m_noteListRef.current?.requestNote('Please enter cancellation reason.', false);
        }

        //Quote Completed status
        this.m_quoteSetAsCompeleted = newValue === 3 && this.state.form.data.quoteStatus !== 3;

        if(newValue === 6) { //Send to sub
            this.onSendToSub();
        }
    }

    private async jobStatus_Change(newValue: MNullable<number>): Promise<void> {
        if(newValue === 9) { //Cancelled
            this.m_noteListRef.current?.requestNote('Please enter cancellation reason.', false);
        }
    }

    private fileSelectionChange(newSelection: FileData[]) {
        this.m_selectedFiles = newSelection;
    }

    //***************************************************************************************************************
    //Send drawing request to sub contractor
    //***************************************************************************************************************
    private onSendToSub(): void {
        this.m_selectedCrews = undefined;
        this.m_crewSelectRef.current!.show('Please select sub contractor for drawing request.');
    }

    private onSendToSubCrewSelectClose(cancelled: boolean): void {
        if(!cancelled) {
            this.m_selectedFiles = [];
            this.m_fileToSubSelectRef.current!.show(this.state.form.data.id,
                'Please select files to send with sub contractor drawing request.');
        }

    }

    private async onSendToSubFileSelectClose(cancelled: boolean): Promise<void> {
        if(!cancelled) {
            await this.m_drawingClient.call(client => client.sendEmail(new SendToSubRequest({
                requestId: this.state.form.data.id,
                crewId: this.m_selectedCrews?.crew.id,
                attachments: this.m_selectedFiles.map(f => f.id)
            })));

            await this.loadPageData();
        }
    }

    //***************************************************************************************************************
    //Send drawing request to sub contractor end
    //***************************************************************************************************************

    private crewSelectionChange(newSelection: CrewContactData | CrewContactData[] | undefined) {
        if(Array.isArray(newSelection)) {
            this.m_selectedCrews = newSelection[0];
        } else {
            this.m_selectedCrews = newSelection;
        }

    }

    private async onFileSelectionComplete(fieldProps: FieldProps, cancelled: boolean): Promise<void> {
        if(cancelled) {
            fieldProps.form.setFieldValue("quoteStatus", this.m_oldQuoteStatus)
        }
        else {
            this.m_noteDialogRef.current!.show('Please enter any user notes for the job', false, (data: string) => this.onNoteEntryComplete(fieldProps, data))
        }
    }

    private async onNoteEntryComplete(fieldProps: FieldProps, newNote: string) {
        if(newNote.length < 1) {
            fieldProps.form.setFieldValue("quoteStatus", this.m_oldQuoteStatus)
        }
        else {
            const createResult = await this.m_noteClient.callWithReply(
                NoteReply, (client) => client.create(new CreateNoteRequest({
                    requestId: this.state.form.data.id,
                    noteText: newNote
                })));
            createResult.throwIfFailed();
            await this.m_formRef.current!.submit();
            await this.m_requestClient.call(client => client.sendQuoteCompletedEmail(new StatusChangeEmail({
                id: this.props.match.params.id,
                fileAttachments: this.m_selectedFiles.length > 0 ? this.m_selectedFiles.map(f => f.id) : [],
                additionalNotes: newNote
            })));
            this.m_noteListRef.current!.refresh();
        }
    }

    private isNavigateDisabled(): boolean{
        if(this.m_locationValue !== null){
            return !this.m_locationValue.match(/^(http(s):\/\/.)[-a-zA-Z0-9@:%._~#=]{2,256}\.[a-z]{2,6}([-a-zA-Z0-9@:%_,.~#?&|//=]*)$/g);
        }

        return true;
    }

    private isQuoteDisabled(): boolean {
        const allowedRoles: string[] = ['Quotation', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return this.isFormDisabled || (!access && this.state.form.data.quoteStatus === 1);
    }

    private isJobBooked(): boolean {
        return this.state.form.data.jobStatus > 0 || this.isFormDisabled;
    }

    private onNavigate_Click(): void {
        window.open(this.m_locationValue, '_blank', 'noopener,noreferrer');
    }

    private onSelectCrew(): void {
        const requestId = encodeURIComponent(this.state.form.data.id);
        const startString = encodeURIComponent(this.state.form.data.jobStartDate!.toISOString());
        const endString = encodeURIComponent(this.state.form.data.jobEndDate!.toISOString());

        this.props.navigate(`/crews?requestId=${requestId}&startDate=${startString}&endDate=${endString}`);
    }

    private onSelectCrewDay(date: Date): void {
        const requestId = encodeURIComponent(this.state.form.data.id);
        const dateString = encodeURIComponent(date.toISOString());

        this.props.navigate(`/crews?requestId=${requestId}&startDate=${dateString}&endDate=${dateString}`);
    }

    private async subJobNavigate(newJob: boolean, id: string = '', subJobCount: number = 0): Promise<void> {
        const subCountText = (Number(subJobCount) + 1).toString().padStart(2, '0');
        if(newJob) {
            const createRequest = new CreateRequestRequest({
                estimate: this.state.form.data.estimate + `-S${subCountText}`,
                additionalRef: '',
                requestor: this.props.msalContext.accounts[0].name,
                exchange: this.state.form.data.exchange,
                slaType: this.state.form.data.slaType,
                projectValue: this.state.form.data.projectValue,
                workStream: this.state.form.data.workStream,
                workDescription: this.state.form.data.workDescription,
                estimatedDays: 0,
                address1: this.state.form.data.address1,
                address2: this.state.form.data.address2,
                address3: this.state.form.data.address3,
                city: this.state.form.data.city,
                county: this.state.form.data.county,
                postcode: this.state.form.data.postcode,
                northing: this.state.form.data.northing,
                easting: this.state.form.data.easting,
                gridReference: this.state.form.data.gridReference,
                parent: this.state.form.data.id
            });

            const result = await this.m_requestClient.callWithReply(RequestReply, (client => client.create(createRequest)));
            result.throwIfFailed();

            this.props.navigate(`/requests/create/${result.reply!.request!.id}`);
        } else {
            this.props.navigate(`/requests/${id}`);
            window.location.reload();
        }
    }

    /** */
    public render(): ReactElement {

        const yesNoList: MDropDownItem<boolean>[] = [{label: 'No', value: false},{label: 'Yes', value: true}];
        const dialogTop: string = this.m_messageShowing ? '260px' : '200px';

        return (
            <MPage title={this.state.pageTitle}
                   subtitle={this.state.pageSubtitle}
                   locked={this.state.page.locked}
                   width={12}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <OkCancelDialog ref={this.m_okCancelDialogRef}
                                okClicked={this.dialog_OkClick}
                />
                <CrewSelectionDialog onSelectionChange={this.crewSelectionChange}
                                     msalContext={this.props.msalContext}
                                     crewTypes={[2]}
                                     ref={this.m_crewSelectRef}
                                     onDialogClose={this.onSendToSubCrewSelectClose}
                />
                <MessageDialog ref={this.m_messageDialogRef}/>
                <AlertDialog ref={this.m_alertDialogRef}/>

                <div style={{position: "fixed", left: 0, width: "66%", top: dialogTop, padding: "0 2rem"}}>
                    <div style={{overflowY: "scroll", overflowX: "hidden", maxHeight: 'calc(100vh - 260px)'}}>
                        <MForm formId={this.state.form.id}
                               initialValues={this.state.form.data}
                               validationSchema={this.state.form.validationSchema}
                               onBeforeSubmit={this.form_onBeforeSubmit}
                               onSubmit={this.form_onSubmit}
                               onAfterSubmit={this.form_onAfterSubmit}
                               onError={this.form_onError}
                               ref={this.m_formRef}
                        >

                            <div className="formgrid grid">
                                <div className="col-12">
                                    <MFormSection title="General">
                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Estimate"
                                                               fieldName="estimate">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.EstimateMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={true}
                                                                  className={fieldClasses}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Requestor"
                                                               fieldName="requestor">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.RequestorMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={true}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Exchange"
                                                               fieldName="exchange">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MAutoComplete id={fieldProps.field.name}
                                                                       fieldProps={fieldProps}
                                                                       disabled={false}
                                                                       className={fieldClasses}
                                                                       suggestions={this.context?.enums === null ? [] : this.context?.enums.exchangeList.map(e => e.name)}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Additional Ref"
                                                               fieldName="additionalRef">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.AdditionalRefMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Project Code"
                                                               fieldName="projectValue">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDown<number> id={fieldProps.field.name}
                                                                           disabled={this.isFormDisabled}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.state.projectList.map(project => ({
                                                                               value: project.id,
                                                                               label: project.code + ' - ' + project.name
                                                                           }))}
                                                                           initialValue={0}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Type Of Works"
                                                               fieldName="workStream">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDown<number> id={fieldProps.field.name}
                                                                           disabled={this.isFormDisabled}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.workStreams.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={0}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Estimated Days"
                                                               fieldName="estimatedDays">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={5}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isQuoteDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>
                                    </MFormSection>
                                    <MFormSection>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label={'SLA'}
                                                               fieldName={'slaType'}>
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MDropDown<number> id="ProjectSelection"
                                                                           disabled={true}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.slaTypes.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={0}
                                                        />
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label={'Due Date'} fieldName={'dueDate'}>
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MCalendar id={fieldProps.field.name}
                                                                   fieldProps={fieldProps}
                                                                   disabled={true}
                                                                   className={fieldClasses}
                                                                   showTime={false}
                                                                   showButtonBar={false}
                                                                   minDate={new Date()}
                                                        />
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label={'Created Date'} fieldName={'submittedDate'}>
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MCalendar id={fieldProps.field.name}
                                                                   fieldProps={fieldProps}
                                                                   disabled={true}
                                                                   className={fieldClasses}
                                                                   showTime={false}
                                                                   showButtonBar={false}
                                                                   minDate={new Date()}
                                                        />
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-12">
                                            <MFormFieldControl label="Work Description (please provide detail if no A55/Job pack is provided)"
                                                               fieldName="workDescription">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextArea id={fieldProps.field.name}
                                                                  maxLength={Request.WorkDescriptionMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}
                                                                   rows={5}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-12">
                                            <MFormFieldControl label="Additional E-mail addresses"
                                                               fieldName={'addRecipients'}>

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextArea id={'addresses'}
                                                                   maxLength={Request.AddRecipientsMaxLength}
                                                                   readonly={false}
                                                                   fieldProps={fieldProps}
                                                                   disabled={this.isFormDisabled}
                                                                   className={fieldClasses}
                                                                   rows={5}
                                                        />
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Quotation">

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Status"
                                                               fieldName="quoteStatus">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <React.Fragment>
                                                            <FileSelectionDialog onSelectionChange={this.fileSelectionChange}
                                                                                 msalContext={this.props.msalContext}
                                                                                 ref={this.m_fileSelectRef}
                                                                                 onDialogClose={(cancelled: boolean) => this.onFileSelectionComplete(fieldProps, cancelled)}
                                                            />
                                                            <NoteDialog ref={this.m_noteDialogRef}/>
                                                            <MDropDown<number> id={fieldProps.field.name}
                                                                               disabled={this.isQuoteDisabled}
                                                                               fieldProps={fieldProps}
                                                                               items={this.state.quoteStatuses.map((enumvalue) => {
                                                                                   return {label: enumvalue.name, value: enumvalue.id};
                                                                               })}
                                                                               initialValue={0}
                                                                               onChange={this.quoteStatus_Change}
                                                            />
                                                        </React.Fragment>
                                                    );
                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Cost Claimable"
                                                               fieldName="costClaimable">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDown<number> id={fieldProps.field.name}
                                                                           disabled={this.isQuoteDisabled}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.costTypes.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={0}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Quote Value"
                                                               fieldName="quoteValue">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MNumericBox id={fieldProps.field.name}
                                                                     maxLength={9}
                                                                     fieldProps={fieldProps}
                                                                     disabled={this.isQuoteDisabled}
                                                                     className={fieldClasses}
                                                                     decimalPlaces={2}
                                                                     mode={'currency'}
                                                                     currency={'GBP'}
                                                                     useGrouping={false}
                                                                     initialValue={-50}
                                                        />
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-12">
                                            <MFormFieldControl label="Job Types"
                                                               fieldName="jobType">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDownMulti<number> id={fieldProps.field.name}
                                                                           disabled={this.isQuoteDisabled}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.jobTypes.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={[]}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-12">
                                            <MFormFieldControl label="TM Detail"
                                                               fieldName="jobDetail">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.JobDetailMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Job Data">
                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label={'No Parking Cones'}
                                                               fieldName={'noParkingRequired'}>
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MDropDown<boolean> id='noParkingRequired'
                                                                            disabled={this.isFormDisabled}
                                                                            fieldProps={fieldProps}
                                                                            items={yesNoList}
                                                                            initialValue={false}
                                                        />
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label={'Bus Stop Suspension'}
                                                               fieldName={'busStop'}>
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MDropDown<number> id='busStop'
                                                                           disabled={this.isFormDisabled}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.busStopValues.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={-1}
                                                        />
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Drawings">
                                        <div className="col-12 md:col-12">
                                            <React.Fragment>
                                                <FileSelectionDialog onSelectionChange={this.fileSelectionChange}
                                                                     msalContext={this.props.msalContext}
                                                                     ref={this.m_fileToSubSelectRef}
                                                                     onDialogClose={this.onSendToSubFileSelectClose}
                                                />
                                                <DrawingControl parentId={this.props.match.params.id}
                                                                msalContext={this.props.msalContext}
                                                                baseDrawingRef={this.state.form.data.drawingRef}
                                                                onSendEmail={this.onSendToSub}
                                                                subDrawingName={this.state.form.data.quoteSubName}
                                                />
                                            </React.Fragment>
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Files">
                                        <div className="col-12 md:col-12">
                                            <FileListControl parentId={this.props.match.params.id}
                                                             msalContext={this.props.msalContext}
                                                             selectionOnly={false}
                                            />
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Location">

                                        <div className="col-12 md:col-6">
                                            <MFormFieldControl label="Easting"
                                                               fieldName="easting">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MNumericBox id={fieldProps.field.name}
                                                                     maxLength={8}
                                                                     fieldProps={fieldProps}
                                                                     disabled={this.isFormDisabled}
                                                                     className={fieldClasses}
                                                                     decimalPlaces={0}
                                                                     useGrouping={false}/>
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-6">
                                            <MFormFieldControl label="Northing"
                                                               fieldName="northing">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MNumericBox id={fieldProps.field.name}
                                                                     maxLength={8}
                                                                     fieldProps={fieldProps}
                                                                     disabled={this.isFormDisabled}
                                                                     className={fieldClasses}
                                                                     decimalPlaces={0}
                                                                     useGrouping={false}/>
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-12">
                                            <MFormFieldControl label="Grid Reference URL"
                                                               fieldName="gridReference">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <React.Fragment>
                                                            <MTextBox id={fieldProps.field.name}
                                                                      maxLength={300}
                                                                      fieldProps={fieldProps}
                                                                      disabled={this.isFormDisabled}
                                                                      className={fieldClasses}
                                                                      onChange={e => this.m_locationValue = e.toString()}
                                                            />
                                                            <MButton
                                                                tooltip={'Browse address'}
                                                                icon={'pi pi-compass'}
                                                                disabled={this.isNavigateDisabled}
                                                                onClick={this.onNavigate_Click}
                                                            />
                                                        </React.Fragment>
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Address">
                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Address 1"
                                                               fieldName="address1">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.Address1MaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Address 2"
                                                               fieldName="address2">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.Address2MaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Address 3"
                                                               fieldName="address3">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.Address3MaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="City"
                                                               fieldName="city">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.CityMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="County"
                                                               fieldName="county">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.CountyMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Postcode"
                                                               fieldName="postcode">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.PostcodeMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label=""
                                                               fieldName="getaddress">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MButton label="Get Address From Location"
                                                                 color="success"
                                                                 iconOnly={false}
                                                                 icon={MNavigateToIconName}
                                                                 disabled={fieldProps.form.values.easting < 100000
                                                                     || fieldProps.form.values.northing < 100000}
                                                                 isBusy={this.isSaving}
                                                                 onClick={e => this.getAddress_Click(fieldProps)}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>
                                    </MFormSection>
                                </div>
                            </div>
                        </MForm>
                    </div>
                </div>

                <div style={{position: "fixed", left: "65%", width: "35%", top: dialogTop, padding: "0 0rem"}}>
                    <div style={{overflowY: "scroll", overflowX: "hidden", maxHeight: 'calc(100vh - 260px)'}}>
                        <MFormSection title="Map">
                            <div className="col-12 md:col-12" style={{width: "100%", height: "35vh"}}>
                                <MGoogleMap lat={this.state.map.lat}
                                            long={this.state.map.lng}
                                            zoom={this.state.map.zoom}
                                            showMarker={this.state.map.showMarker}
                                />
                            </div>
                        </MFormSection>

                        <Divider/>

                        <MFormSection title="Notes">
                            <div className="col-12 md:col-12">
                                <NoteListControl parentId={this.props.match.params.id}
                                                 msalContext={this.props.msalContext}
                                                 ref={this.m_noteListRef}
                                />
                            </div>
                        </MFormSection>

                        <Divider/>

                        <MFormSection title="Audit">
                            <div className="col-12 md:col-12">
                                <AuditLogControl parentId={this.props.match.params.id}
                                                 msalContext={this.props.msalContext} />
                            </div>
                        </MFormSection>
                    </div>
                </div>

            </MPage>

        );

    }

    private get isFormDisabled(): boolean{
        return this.isSaving;
    }

    private get isExecutingAction(): boolean {
        return this.state.pageActions !== PageActions.None;
    }

    /** */
    private get isSaving() {
        return (this.state.pageActions & PageActions.Saving) === PageActions.Saving;
    }

    private get isRefreshing() {
        return (this.state.pageActions & PageActions.Refreshing) === PageActions.Refreshing;
    }
}

export default withMsal(withRouter(EditRequestPage));
